import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Section } from "@/components/Primitives";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const Email = () => {
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState("Sales Dashboard — Executive Update");
  const [body, setBody] = useState("Please find the latest sales performance overview attached.\n\nRegards,\nMaison Analytica");
  const [attachment, setAttachment] = useState("dashboard_pdf");
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadLogs = () => api.get("/email/logs").then((r) => setLogs(r.data.rows || []));
  useEffect(() => { loadLogs(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const list = recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Enter at least one recipient"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/email/send", {
        recipients: list, subject, body, attachment_kind: attachment,
      });
      toast.success("Email queued — " + data.note);
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Email Center"
        title="Share the insight"
        subtitle="Compose and queue reports for recipients. Delivery logs below — connect Resend or SendGrid to begin real sending."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" title="Compose">
          <form data-testid="email-compose-form" onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Recipients (comma-separated)</label>
              <input
                data-testid="email-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)}
                placeholder="cfo@maison.com, regional@maison.com"
                className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Subject</label>
              <input
                data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Body</label>
              <textarea
                data-testid="email-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)}
                className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Attachment</label>
              <div className="mt-2 flex gap-2">
                {[
                  { k: "dashboard_pdf", l: "Dashboard PDF" },
                  { k: "dashboard_excel", l: "Dashboard Excel" },
                  { k: "none", l: "None" },
                ].map((o) => (
                  <button
                    key={o.k} type="button"
                    data-testid={`email-attach-${o.k}`}
                    onClick={() => setAttachment(o.k)}
                    className={`text-xs uppercase tracking-wider px-3 py-2 rounded-sm border transition-all ${
                      attachment === o.k ? "border-myb-500 text-myb-500 bg-myb-50" : "border-myb-100 text-muted-foreground"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <button
              data-testid="email-send-btn"
              disabled={busy}
              className="inline-flex items-center gap-2 bg-myb-500 text-white hover:bg-myb-400 px-5 py-2.5 rounded-sm disabled:opacity-50 transition-all text-sm"
            >
              <PaperPlaneTilt weight="fill" size={14} /> {busy ? "Queueing…" : "Send"}
            </button>
          </form>
        </Card>

        <Card title="Configure provider">
          <div className="text-sm text-muted-foreground space-y-3">
            <div className="inline-flex items-center gap-2 text-myb-500">
              <EnvelopeSimple weight="duotone" size={18} /> Mock mode active
            </div>
            <p className="leading-relaxed">
              All outgoing emails are currently logged for audit. To send real emails, add a <span className="text-myb-500">Resend</span> or <span className="text-myb-500">SendGrid</span> API key and wire <code className="text-xs bg-myb-50/50 px-1 rounded-sm">/api/email/send</code>.
            </p>
          </div>
        </Card>
      </div>

      <Section eyebrow="Delivery" title="Email logs">
        <Card testId="email-logs">
          {logs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No emails yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b hairline">
                  {["When", "Status", "Recipients", "Subject", "By"].map((h) => (
                    <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t hairline">
                    <td className="py-2 px-3 text-muted-foreground text-xs">{new Date(l.sent_at).toLocaleString()}</td>
                    <td className="py-2 px-3 text-xs">
                      <span className="text-myb-500 border border-myb-200 rounded-full px-2 py-0.5 uppercase tracking-wider">{l.status}</span>
                    </td>
                    <td className="py-2 px-3 text-xs">{(l.recipients || []).join(", ")}</td>
                    <td className="py-2 px-3">{l.subject}</td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{l.sent_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </Section>
    </div>
  );
};

export default Email;
